import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import { useStageGuard } from "../useStageGuard";
import { useEligibilityData } from "../useEligibilityData";
import styles from "../index.module.css";

let Digio: any;

function EligibilityAgreement() {
  const navigate = useNavigate();

  const userData = useEligibilityData();

  const mobile = userData?.mobile || "";

  const fromSelfie = new URLSearchParams(window.location.search).get("from") === "selfie";
  useStageGuard(userData?.userId, userData?.applicationId, 11, fromSelfie);

  const [authToken, setAuthToken] = useState<{ value: string }>({ value: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userData?.userId) return;
    let cancelled = false;
    const eligToken = sessionStorage.getItem("eligibility_auth_token");
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/generate-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(eligToken ? { Authorization: `Bearer ${eligToken}` } : {}),
      },
      body: JSON.stringify({
        mobile,
        userId: userData?.userId,
        applicationId: userData?.applicationId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setAuthToken({ value: data.data });
        return fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/auth`, {
          headers: {
            Authorization: `Bearer ${data.data}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
      })
      .then((res) => res?.json())
      .then((data) => {
        if (cancelled || !data) return;
        const agreementStatus = data.data?.agreementStatus;
        if (agreementStatus === 3 || agreementStatus === 9) {
          navigate("/eligibility/emandate", { replace: true, state: { data: userData } });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userData?.userId, userData?.applicationId]);

  const fetchJsFromCDN = (src: string, externals: string[]) =>
    new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.setAttribute("src", src);
      script.addEventListener("load", () =>
        resolve(externals.map((key) => (window as any)[key]))
      );
      script.addEventListener("error", reject);
      document.body.appendChild(script);
    });

  const handleAgreementSuccess = () => {
    navigate("/eligibility/emandate", { replace: true, state: { data: userData } });
  };

  const handleAgreement = () => {
    if (!authToken.value) {
      setError("Please wait while we load the signing service.");
      return;
    }
    setLoading(true);
    setError("");
    fetchJsFromCDN(process.env.REACT_APP_DIGIO_SDK as string, ["Digio"])
      .then((digio: any) => {
        fetch(`${process.env.REACT_APP_DASHBOARD_URL}/agreement/generate`, {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken.value}` },
          redirect: "follow",
        })
          .then((res) => res.json())
          .then((result) => {
            const { aadhaar, email, id, tokenId } = result.data;
            const userIdentifier = aadhaar || email;
            Digio = digio[0];
            const d = new Digio({
              environment: process.env.REACT_APP_DIGIO_ENV,
              logo: "yourlogourl",
              theme: { primaryColor: "#234FDA", secondaryColor: "#234FDA" },
              digioDocumentId: id,
              digioUserIdentifier: userIdentifier,
              is_iframe: true,
              callback: (_digio: any) => {
                setLoading(false);
                if (
                  _digio.error_code !== undefined &&
                  _digio.error_code !== "CANCELLED"
                ) {
                  setError(_digio.message || "Signing failed. Please try again.");
                  return;
                }
                handleAgreementSuccess();
              },
            });
            d.init();
            d.submit(id, userIdentifier, tokenId);
          })
          .catch(() => {
            setLoading(false);
            setError("Failed to load agreement. Please try again.");
          });
      })
      .catch(() => {
        setLoading(false);
        setError("Failed to load signing SDK. Please try again.");
      });
  };

  const features = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z"
            stroke="#d32028"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M9 12L11 14L15 10" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      label: "Legally",
      desc: "Binding",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="11" width="18" height="11" rx="2" stroke="#d32028" strokeWidth="1.7" />
          <path d="M7 11V7C7 4.79 9.24 3 12 3C14.76 3 17 4.79 17 7V11" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1.5" fill="#d32028" opacity="0.6" />
        </svg>
      ),
      label: "Secure",
      desc: "Encrypted",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 19L19 12L22 15L15 22L12 19Z" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M18 13L16.5 5.5L2 2L5.5 16.5L13 18L18 13Z" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="9" cy="9" r="2" stroke="#d32028" strokeWidth="1.5" />
        </svg>
      ),
      label: "e-Sign",
      desc: "Paperless",
    },
  ];

  const checkItems = [
    "Review the loan terms carefully before signing",
    "Aadhaar-linked mobile is accessible for OTP",
    "Signing is legally binding — read all clauses",
  ];

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div
                  style={{
                    width: "2.4rem",
                    height: "2.4rem",
                    borderRadius: "50%",
                    background: "#F4E5DD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14 2H6C4.895 2 4 2.895 4 4V20C4 21.105 4.895 22 6 22H18C19.105 22 20 21.105 20 20V8L14 2Z"
                      fill="#d32028"
                      opacity="0.15"
                      stroke="#d32028"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <polyline points="14,2 14,8 20,8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M9 13H15M9 17H12" stroke="#d32028" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Loan Agreement</p>
              </div>

              {/* ── Hero illustration ── */}
              <div style={{ display: "flex", justifyContent: "center", padding: "0.3rem 0 0.2rem" }}>
                <svg width="120" height="104" viewBox="0 0 200 155" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="100" cy="82" rx="72" ry="58" fill="#fff5f5" />
                  <rect x="52" y="20" width="80" height="106" rx="8" fill="white" stroke="#f9d8d6" strokeWidth="1.5" />
                  <rect x="52" y="20" width="80" height="20" rx="8" fill="#fde8e8" />
                  <rect x="52" y="32" width="80" height="8" fill="#fde8e8" />
                  <rect x="65" y="26" width="40" height="5" rx="2.5" fill="#d32028" opacity="0.4" />
                  <rect x="62" y="52" width="60" height="4" rx="2" fill="#f0e4e4" />
                  <rect x="62" y="61" width="52" height="4" rx="2" fill="#f0e4e4" />
                  <rect x="62" y="70" width="56" height="4" rx="2" fill="#f0e4e4" />
                  <rect x="62" y="79" width="44" height="4" rx="2" fill="#f0e4e4" />
                  <rect x="62" y="88" width="58" height="4" rx="2" fill="#f0e4e4" />
                  <rect x="62" y="97" width="38" height="4" rx="2" fill="#f0e4e4" />
                  <line x1="62" y1="108" x2="122" y2="108" stroke="#f9d8d6" strokeWidth="1" strokeDasharray="3 3" />
                  <rect x="62" y="112" width="36" height="8" rx="3" fill="#fde8e8" />
                  <path d="M66 116C68 113 70 119 73 116C76 113 78 119 81 116C83 113 85 119 88 116" stroke="#d32028" strokeWidth="1.3" strokeLinecap="round" opacity="0.7" />
                  <rect x="104" y="112" width="18" height="8" rx="3" fill="#f0f0f0" />
                  <g transform="translate(116, 24)">
                    <circle cx="20" cy="20" r="20" fill="#d32028" />
                    <circle cx="20" cy="20" r="20" fill="white" opacity="0.12" />
                    <path
                      d="M26 12L28 14L18 24L14 26L16 22L26 12Z"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <path d="M24 14L26 16" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                  </g>
                  <circle cx="44" cy="42" r="4" fill="#fde8e8" />
                  <circle cx="160" cy="118" r="5" fill="#fde8e8" />
                  <circle cx="155" cy="30" r="3" fill="#f9d8d6" />
                  <circle cx="38" cy="118" r="3" fill="#fde8e8" />
                  <circle cx="168" cy="60" r="2.5" fill="#f9d8d6" />
                </svg>
              </div>

              {/* ── Description ── */}
              <p
                style={{
                  textAlign: "center",
                  fontSize: "0.86rem",
                  color: "#666",
                  margin: "0 0 0.65rem",
                  lineHeight: 1.55,
                }}
              >
                Review and digitally sign your loan agreement.
              </p>

              {/* ── Feature tiles ── */}
              <div style={{ display: "flex", gap: "0.55rem", marginBottom: "0.65rem" }}>
                {features.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      flex: 1,
                      background: "#fff9f9",
                      border: "1px solid #f9d8d6",
                      borderRadius: "10px",
                      padding: "0.55rem 0.4rem",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "0.2rem" }}>
                      {item.icon}
                    </div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#222", marginBottom: "0.12rem" }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: "0.67rem", color: "#999" }}>{item.desc}</div>
                  </div>
                ))}
              </div>

              {/* ── Keep ready checklist ── */}
              <div
                style={{
                  background: "#fafafa",
                  border: "1px solid #efefef",
                  borderRadius: "10px",
                  padding: "0.65rem 1rem",
                }}
              >
                <p style={{ fontSize: "0.82rem", fontWeight: 700, color: "#333", margin: "0 0 0.35rem" }}>
                  Before you sign:
                </p>
                {checkItems.map((item) => (
                  <div
                    key={item}
                    style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.25rem" }}
                  >
                    <div
                      style={{
                        width: "13px",
                        height: "13px",
                        borderRadius: "50%",
                        background: "#d32028",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <svg width="8" height="7" viewBox="0 0 9 7" fill="none">
                        <path
                          d="M1.5 3.5L3.5 5.5L7.5 1.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span style={{ fontSize: "0.66rem", color: "#555" }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* ── Powered by Digio ── */}
              <p
                style={{
                  fontSize: "0.68rem",
                  color: "#bbb",
                  textAlign: "center",
                  marginTop: "0.5rem",
                  marginBottom: 0,
                }}
              >
                Powered by{" "}
                <img
                  src="https://www.digio.in/images/digio_blue.png"
                  alt="Digio"
                  style={{ height: "0.85rem", verticalAlign: "middle", marginLeft: "0.2rem" }}
                />
              </p>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
            </div>

            {/* ── Proceed + Skip ── */}
            <div
              style={{
                padding: "0rem 1.25rem 1rem",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.6rem",justifyContent:'center'
              }}
            >
              <button
                onClick={handleAgreement}
                disabled={loading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1.5rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Loading..." : (
                  <>
                    Sign Agreement
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/emandate", { replace: true, state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityAgreement;
